import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Collapse,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import { Close, ExpandMore, ExpandLess, MonetizationOn, GitHub, Language, Videocam, Slideshow } from '@mui/icons-material';
import { useApiClient } from '../services/api';
import { format } from 'date-fns';
import PayoutModal from './PayoutModal';

interface BountySubmissionsModalProps {
  open: boolean;
  onClose: () => void;
  bountyId: string;
  bountyTitle: string;
  bountyValue: number;
  bountyCurrency: string;
}

interface TeamSubmission {
  id: string;
  team_name: string;
  project_title?: string;
  demo_description?: string;
  github_repo_link?: string;
  demo_url?: string;
  video_url?: string;
  slides_url?: string;
  tech_stack?: string;
  submitted_at: string;
  participants: Array<{
    id: string;
    name: string;
    email: string;
    linkedin?: string;
  }>;
  submissionCount: number;
}

const BountySubmissionsModal: React.FC<BountySubmissionsModalProps> = ({
  open,
  onClose,
  bountyId,
  bountyTitle,
  bountyValue,
  bountyCurrency,
}) => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<TeamSubmission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Payout modal state
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamSubmission | null>(null);

  useEffect(() => {
    if (open) {
      fetchSubmissions();
    }
  }, [open, bountyId]);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getBountySubmissions(bountyId);

      if (response.success && response.data) {
        setSubmissions(response.data);
      } else {
        setError(response.message || 'Failed to load submissions');
      }
    } catch (err) {
      setError('Failed to load submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandRow = (teamId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  const handleSendPayout = (team: TeamSubmission) => {
    setSelectedTeam(team);
    setPayoutModalOpen(true);
  };

  const handlePayoutSuccess = () => {
    // Refresh submissions to show updated payout status
    fetchSubmissions();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" component="div">
                Submissions for {bountyTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bounty Value: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: bountyCurrency,
                }).format(bountyValue)}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && submissions.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No submissions yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Teams will appear here once they submit their projects for this bounty.
              </Typography>
            </Box>
          )}

          {!loading && !error && submissions.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((submission) => {
                    const isExpanded = expandedRows.has(submission.id);
                    return (
                      <React.Fragment key={submission.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandRow(submission.id)}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {submission.team_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {submission.submissionCount} bounties total
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {submission.project_title || 'Untitled Project'}
                            </Typography>
                            {submission.demo_description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {submission.demo_description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                              {submission.participants.map((participant) => (
                                <Tooltip key={participant.id} title={participant.name}>
                                  <Avatar
                                    sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                                  >
                                    {participant.name.charAt(0).toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {format(new Date(submission.submitted_at), 'MMM dd, yyyy')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(submission.submitted_at), 'h:mm a')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<MonetizationOn />}
                              onClick={() => handleSendPayout(submission)}
                              sx={{ minWidth: '120px' }}
                            >
                              Send Payout
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row Details */}
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Team Members
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                  {submission.participants.map((participant) => (
                                    <Chip
                                      key={participant.id}
                                      label={participant.name}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>

                                {submission.tech_stack && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Tech Stack
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                      {submission.tech_stack}
                                    </Typography>
                                  </>
                                )}

                                <Typography variant="subtitle2" gutterBottom>
                                  Project Links
                                </Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                  {submission.github_repo_link && (
                                    <Button
                                      size="small"
                                      startIcon={<GitHub />}
                                      href={submission.github_repo_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                    >
                                      GitHub
                                    </Button>
                                  )}
                                  {submission.demo_url && (
                                    <Button
                                      size="small"
                                      startIcon={<Language />}
                                      href={submission.demo_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                    >
                                      Demo
                                    </Button>
                                  )}
                                  {submission.video_url && (
                                    <Button
                                      size="small"
                                      startIcon={<Videocam />}
                                      href={submission.video_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                    >
                                      Video
                                    </Button>
                                  )}
                                  {submission.slides_url && (
                                    <Button
                                      size="small"
                                      startIcon={<Slideshow />}
                                      href={submission.slides_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                    >
                                      Slides
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payout Modal */}
      {selectedTeam && (
        <PayoutModal
          open={payoutModalOpen}
          onClose={() => {
            setPayoutModalOpen(false);
            setSelectedTeam(null);
          }}
          team={selectedTeam}
          bountyId={bountyId}
          bountyTitle={bountyTitle}
          suggestedAmount={bountyValue}
          currency={bountyCurrency}
          onSuccess={handlePayoutSuccess}
        />
      )}
    </>
  );
};

export default BountySubmissionsModal;
